import { ArrowRightIcon } from '@phosphor-icons/react';
import { useRef } from 'react';
import { Reveal } from '@/components/reveal';
import { Section, shellClass } from '@/components/section';
import { SectionHeader, playHeader } from '@/components/section-header';
import { Button } from '@/components/ui/button';
import { copy } from '@/content/copy';
import { cn } from '@/lib/cn';
import { DOCS_URL } from '@/lib/env';
import { useRevealOnEnter } from '@/scroll/use-reveal-on-enter';

const { api } = copy;
const { code } = api;

/** Line number gutter of the code box. */
function LineNumber({ n }: { n: number }) {
  return (
    <span
      aria-hidden
      className="mr-5 inline-block w-4 select-none text-right text-muted-foreground/40"
    >
      {n}
    </span>
  );
}

/**
 * The integration story beside one request. The code box types itself in a line at a time
 * once the section enters; the capability chips land with the header.
 */
export function Api() {
  const ref = useRef<HTMLElement>(null);

  useRevealOnEnter(ref, (tl, q) => {
    const at = playHeader(tl, q);
    tl.to(q('[data-point]'), { opacity: 1, y: 0, duration: 0.5, stagger: 0.06 }, at)
      .to(q('[data-code]'), { opacity: 1, y: 0, duration: 0.9 }, at - 0.3)
      .to(q('[data-code-line]'), { opacity: 1, y: 0, duration: 0.35, stagger: 0.08 }, at + 0.1);
  });

  const lastField = code.fields.length - 1;

  return (
    <Section ref={ref} id="api" tone="card">
      <div
        className={cn(
          shellClass,
          'grid gap-14 lg:grid-cols-[1fr_1.05fr] lg:items-center lg:gap-16',
        )}
      >
        <div className="grid gap-8">
          <SectionHeader size="column" eyebrow={api.eyebrow} title={api.title} lede={api.lede} />
          <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
            {api.points.map((point) => (
              <li key={point}>
                <Reveal
                  as="span"
                  data-point
                  className="inline-flex rounded-lg border border-border px-3 py-2 text-xs font-medium text-foreground/85"
                >
                  {point}
                </Reveal>
              </li>
            ))}
          </ul>
          <Reveal data-point>
            <Button
              href={DOCS_URL}
              size="sm"
              icon={<ArrowRightIcon aria-hidden weight="bold" className="h-3.5 w-3.5" />}
            >
              {api.docs}
            </Button>
          </Reveal>
        </div>

        <div className="relative">
          <div aria-hidden className="panel-glow absolute -inset-10 -z-10" />
          <Reveal
            data-code
            role="group"
            aria-label={code.label}
            className="surface overflow-hidden rounded-3xl"
          >
            <div className="flex items-center justify-between gap-4 border-b border-border px-6 py-4 font-mono text-xs">
              <span className="flex items-center gap-3">
                <span className="rounded-md bg-primary/20 px-2 py-1 font-semibold text-brand-sky">
                  {code.method}
                </span>
                <span className="text-foreground/85">{code.path}</span>
              </span>
              <span className="flex items-center gap-2 uppercase tracking-[0.12em] text-brand-sky">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-brand-sky" />
                {code.status}
              </span>
            </div>
            <pre className="m-0 overflow-x-auto p-6 font-mono text-[0.82rem] leading-7">
              <code>
                <Reveal as="span" data-code-line className="block">
                  <LineNumber n={1} />
                  {'{'}
                </Reveal>
                {code.fields.map((field, index) => (
                  <Reveal as="span" data-code-line key={field.key} className="block">
                    <LineNumber n={index + 2} />
                    <span className="pl-6 text-brand-mist">{`"${field.key}"`}</span>
                    <span className="text-muted-foreground">: </span>
                    <span
                      className={field.kind === 'string' ? 'text-brand-sky' : 'text-foreground'}
                    >
                      {field.kind === 'string' ? `"${field.value}"` : field.value}
                    </span>
                    {index < lastField ? <span className="text-muted-foreground">,</span> : null}
                  </Reveal>
                ))}
                <Reveal as="span" data-code-line className="block">
                  <LineNumber n={code.fields.length + 2} />
                  {'}'}
                </Reveal>
              </code>
            </pre>
          </Reveal>
        </div>
      </div>
    </Section>
  );
}
